import type { CatalogModel, PriceEntry } from "@models/providerCatalog";

export type RateField = "input_mtok" | "output_mtok" | "page" | "image" | "batch_factor";

/** The rates that mean something for a capability (what its usage events carry). */
export const RATE_FIELDS: Record<string, RateField[]> = {
  chat: ["input_mtok", "output_mtok", "batch_factor"],
  embedding: ["input_mtok", "batch_factor"],
  image_generation: ["image", "input_mtok", "output_mtok"],
  ocr: ["page"],
};
export const ALL_RATES: RateField[] = ["input_mtok", "output_mtok", "page", "image", "batch_factor"];

/**
 * Capabilities measured in tokens, pages or images have their own rates; any other one
 * (translation, speech, rerank) reports a unit of its own and is priced per unit (`units`).
 */
export const usesUnits = (capability: string): boolean => !(capability in RATE_FIELDS);

export const ratesFor = (capability: string): RateField[] => RATE_FIELDS[capability] ?? [];

/** `characters $0.00002 · audio_seconds $0.0001`. */
export const unitsText = (units: Record<string, number> | null | undefined): string =>
  units && Object.keys(units).length
    ? Object.entries(units).map(([unit, rate]) => `${unit} ${usd(rate)}`).join(" · ")
    : "-";

/** USD with as many decimals as small rates need (0.000125 stays readable). */
export const usd = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  const digits = value === 0 ? 2 : value < 0.01 ? 6 : value < 1 ? 4 : 2;
  return `$${Number(value.toFixed(digits))}`;
};

export const rateText = (field: RateField, value: number | null | undefined): string =>
  field === "batch_factor" ? (value == null ? "-" : `×${value}`) : usd(value);

/** The price a model is charged at now: the admin entry if any, else the library rates. */
export const effectiveRates = (model: CatalogModel): Partial<Record<RateField, number | null>> | null => {
  if (model.admin_price) return model.admin_price as Partial<Record<RateField, number | null>>;
  if (model.library_price) {
    return { input_mtok: model.library_price.input_mtok, output_mtok: model.library_price.output_mtok };
  }
  return null;
};

/** One line for a table cell, e.g. `$0.3 in · $2.5 out` or `$0.01 / page`. */
export const priceSummary = (model: CatalogModel): string | null => {
  const rates = effectiveRates(model);
  if (!rates) return null;
  const parts: string[] = [];
  if (rates.input_mtok != null) parts.push(`${usd(rates.input_mtok)} in`);
  if (rates.output_mtok != null) parts.push(`${usd(rates.output_mtok)} out`);
  if (rates.page != null) parts.push(`${usd(rates.page)} / page`);
  if (rates.image != null) parts.push(`${usd(rates.image)} / image`);
  const units = model.admin_price?.units;
  if (units) parts.push(...Object.entries(units).map(([unit, rate]) => `${usd(rate)} / ${unit}`));
  return parts.join(" · ") || null;
};

export type { PriceEntry };
